import { useAuth0 } from '@auth0/auth0-react';
import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import SimpleHttpService from '../../../core/generic/simple-http-service.port.ts';
import Loading from '../public/loading/Loading.tsx';
import PublicLayoutPage from '../public/PublicLayoutPage.tsx';
import { useInjection } from '../ioc/use-injection.ts';
import INJECTION_TOKENS from '../../../core/generic/ioc/injection-tokens.ts';
import { CanUserAccessUsecase } from '../../../core/usecase/can-user-access-usecase.ts';
import HttpService from '../../../core/generic/http-service.port.ts';
import { CreateUserUsecase } from '../../../core/usecase/create-user-usecase.ts';

type Status = 'done' | 'pending' | 'undefined';

const PrivateRoute = ({ component: Component }: { component: FunctionComponent }): ReactElement => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  } = useAuth0();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isAccessCheckProgressing, setIsAccessCheckProgressing] = useState<Status>('undefined');

  const useCase = useInjection<CanUserAccessUsecase>(INJECTION_TOKENS.CAN_USER_ACCESS_USECASE);
  const metaHttpClient = useInjection<HttpService>(INJECTION_TOKENS.META_API_HTTP_SERVICE);
  const dv360HttpClient = useInjection<SimpleHttpService>(INJECTION_TOKENS.DV360_API_HTTP_SERVICE);
  const createUserUseCase = useInjection<CreateUserUsecase>(INJECTION_TOKENS.CREATE_USER_USECASE);

  const domain = process.env.REACT_APP_AUTH0_DOMAIN;

  useEffect(() => {
    const check = async () => {
      if (!isAuthenticated) return;

      try {
        setIsAccessCheckProgressing('pending');

        if (!metaHttpClient.checkAccessToken() || !dv360HttpClient.checkAccessToken()) {
          let accessToken: string;
          try {
            accessToken = await getAccessTokenSilently();
          } catch (authError: unknown) {
            const err = authError as { error?: string; message?: string };
            const isRecoverable =
              err.error === 'login_required' ||
              err.error === 'consent_required' ||
              err.error === 'missing_refresh_token' ||
              err.message?.includes('Missing Refresh Token');
            if (isRecoverable) {
              await loginWithRedirect({
                appState: { returnTo: window.location.pathname },
              });
              return;
            }
            throw authError;
          }

          const idToken = await getIdTokenClaims();
          metaHttpClient.initialize(accessToken, idToken?.__raw as unknown as string);
          dv360HttpClient.initialize(accessToken);
        }

        const hasAccess = await useCase.execute();
        await createUserUseCase.execute();
        setIsAuthorized(hasAccess);
      } finally {
        setIsAccessCheckProgressing('done');
      }
    };

    check();
  }, [
    useCase,
    createUserUseCase,
    isAuthenticated,
    metaHttpClient,
    dv360HttpClient,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    domain,
  ]);

  if (!isLoading) {
    if (!isAuthenticated) {
      return <Navigate to="sign-in" replace />;
    } else if (isAccessCheckProgressing !== 'done') {
      return <PublicLayoutPage component={Loading} />;
    } else if (!isAuthorized) {
      return <Navigate to="not-authorized" replace />;
    } else {
      return <Component />;
    }
  } else {
    return <PublicLayoutPage component={Loading} />;
  }
};

export default PrivateRoute;
